import React, { useState, useEffect, useCallback } from 'react';
import { generateSunoPrompt, regenerateWithAdvice } from '@services/llmDispatcher';
import { getProvider, buildSunoInjectionScript, resolveStyleTags } from '@services/llmShared';
import { ensureUniqueSongTitle } from '@/utils/uniqueSongTitle';
import { Header } from '@components/Header';
import { Footer } from '@components/Footer';
import { AdminPage } from '@components/AdminPage/AdminPage';
import { ToastProvider } from '@components/ui';
import { CompareView } from '@components/Compare/CompareView';
import { VaultPicker } from '@components/Compare/VaultPicker';
import { ModeProvider, useMode, useModeAware, AdaptiveTabNav } from '@features/ModeSystem';
import { HookhouseFormData, ParsedSunoOutput, SunoClip, MusicCritique } from '@/types';
import { Library, Clock, ChevronRight, Keyboard } from 'lucide-react';
import { VocalConfigProvider, useVocalConfig } from '@context/VocalConfigContext';
import { ProductionConfigProvider, useProductionConfig } from '@context/ProductionConfigContext';
import { CompareProvider, useCompare, useCreateCompareSlot } from '@context/CompareContext';
import { TemplateProvider } from '@context/TemplateContext';
import { StudioFormProvider, useStudioForm } from '@context/StudioFormContext';
import { SoloChoreographerProvider, useSoloChoreographer } from '@features/SoloChoreographer';
import { RiffDnaProvider, useRiffDna } from '@context/RiffDnaContext';
import { ReferenceTrackProvider } from '@context/ReferenceTrackContext';
import { postProcessLyrics, postProcessRiffDna, postProcessPhysicsLayers } from '@utils/lyricsPostProcessor';
import { deliveryProblems } from '@services/deliveryChecks';
import { saveToVault, updateDraftField } from '@utils/vaultSave';
import { HookHouseTemplate, TemplateConfig } from '@/types/template.types';
import { getInstrumentById } from '@data/instrumentLibrary';
import { getMixTemplateById } from '@data/mixPositionTemplates';
import { useMixTemplates } from '@hooks/useMixTemplates';
import { MUSICAL_KEYS } from '@data/moodKeyMappings';
import { TemplatePanel } from '@components/Templates/TemplatePanel';
import { SongSeed } from '@components/RiffWriter/types';
import { useGenerationSessions } from '@hooks/useGenerationSessions';
import { ShareCodeModal } from '@components/ShareCode/ShareCodeModal';
import { ImportCodeModal } from '@components/ShareCode/ImportCodeModal';
import { SessionMemoryPanel } from '@components/SessionMemory/SessionMemoryPanel';
import { SaveSessionModal } from '@components/SessionMemory/SaveSessionModal';
import { useSessionMemory } from '@hooks/useSessionMemory';
import { SessionMemorySnapshot } from '@/types/sessionMemory.types';
import { useModalStates } from '@hooks/useModalStates';
import { useSunoIntegration } from '@hooks/useSunoIntegration';
import { useStudioTeam } from '@hooks/useStudioTeam';
import { useSuiteBridge } from '@hooks/useSuiteBridge';
import { useVariationChainHandlers } from '@hooks/useVariationChainHandlers';
import { TabRenderer } from '@components/TabRenderer';
import { useGlobalShortcuts } from '@hooks/useGlobalShortcuts';
import { ShortcutReferencePanel } from '@components/ShortcutReferencePanel';
import SunoPrepModal from '@components/SunoPrep/SunoPrepModal';
import GenerationProgress from '@components/SunoPrep/GenerationProgress';
import type { NarrativeBlueprint } from '@/types/narrative.types';
import { buildNarrativeBlock } from '@/hooks/useNarrativeEngine';
import { getGenreFamily } from '@components/RiffWriter/genreFamilies';
import { fetchWithCsrf } from '@/utils/fetchWithCsrf';
import type { SituationCard } from '@hooks/useStoryEngine';
import { usePersona } from '@hooks/usePersona';
import { PersonaWizard } from '@components/Persona/PersonaWizard';
import { PersonaPicker } from '@components/Persona/PersonaPicker';
import type { PersonaIdentityPayload, PersonaWizardData } from '@/types/persona.types';
import { useLyricOrchestrator } from '@hooks/useLyricOrchestrator';
import { toLyricOutput } from '@services/lyricOrchestratorService';
import { useLyricPolisher } from '@hooks/useLyricPolisher';
import { toPolishOutput } from '@services/lyricPolishService';
import { takeScaffold } from '@/utils/polishHandoff';
import { squeezeBlock1Overhead } from '@/shared/block1Overhead';
import { attachStudioTeamScaffold } from '@/shared/studioTeamScaffold';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import CallbackPage from './pages/CallbackPage';
import { useCsrfToken } from '@hooks/useCsrfToken';
import { migrateLocalStorageToDb } from '@utils/dbMigration';
import { trimLyricsBodyToHardCap } from '@/shared/lyricsHardCap';
import { squeezeForgeBlocks } from '@/shared/forgeOverhead';

// Same ceiling/target pair Studio Team's server-side pipeline uses
// (server/routes/studioteam.routes.ts) — no principled reason for
// RiffWriter's absolute Suno ceiling to differ.
const LYRICS_ABS_MAX = 5000;
const LYRICS_TARGET_MAX = 4920;

/**
 * Final pass after all post-processing (physics layers, solo, Riff DNA):
 *
 * 1. Hard-cap lyricsWithTags if post-processing pushed it over Suno's
 *    ceiling. RiffWriter had no equivalent of Studio Team's
 *    enforceLyricsHardCap — Riff DNA's prepend and physics-layer injection
 *    can push an already-under-limit LLM output over 5000 chars with
 *    nothing downstream to catch it, so the over-limit text shipped
 *    straight to the Vault uncapped. Drops trailing lyrical lines one at a
 *    time (never a [Section] tag, never the metadata bleed token) rather
 *    than a blind character chop.
 * 2. Rebuild the Suno browser-console injection script from the FINAL
 *    style/lyrics/title. The script embedded on `result` by
 *    parseResponse()/toLyricOutput() is a snapshot taken right after the
 *    LLM responds — before Riff DNA's append to `style` and before the
 *    hard-cap above — so what actually got pasted into Suno was previously
 *    missing whatever that append added, or truncated mid-sentence by
 *    buildSunoInjectionScript's own blind `.substring(0, 5000)` when lyrics
 *    ran long.
 *
 * Call this last, after every post-process step, so both the Vault copy
 * and the Suno-bound copy match and neither exceeds the ceiling.
 */
function finalizeGeneratedResult(result: ParsedSunoOutput): ParsedSunoOutput {
  // Shorten the Forge blocks first (solo detail, scream, Deep-Dive layers), then cue/dossier overhead, so the
  // trim below never has to delete sung lines it could have spared (AUTO-086).
  const forgeSqueezed = squeezeForgeBlocks(result.lyricsWithTags, LYRICS_ABS_MAX, LYRICS_TARGET_MAX);
  if (forgeSqueezed.steps.length > 0) {
    console.info(`[forge-squeeze] shortened ${forgeSqueezed.steps.join(', ')} to keep the sung lines (${result.lyricsWithTags.length} -> ${forgeSqueezed.body.length} chars).`);
  }
  const squeezed = squeezeBlock1Overhead(forgeSqueezed.body, LYRICS_ABS_MAX, LYRICS_TARGET_MAX).body;
  const capped = trimLyricsBodyToHardCap(squeezed, LYRICS_ABS_MAX, LYRICS_TARGET_MAX);
  const lyricsWithTags = capped.hardTrimmed ? capped.body : squeezed;
  if (capped.hardTrimmed) {
    console.warn(`[riffwriter] Lyrics hard-capped to ${capped.finalChars} chars (was over ${LYRICS_ABS_MAX}) after post-processing.`);
  }
  return {
    ...result,
    lyricsWithTags,
    javascriptCode: buildSunoInjectionScript(
      result.title,
      resolveStyleTags(result),
      lyricsWithTags,
      result.weirdness ?? 65,
      result.styleStrength ?? 85,
    ),
  };
}

/**
 * Auth guard — shows loading spinner during auth check,
 * redirects to /login if not authenticated,
 * renders children if authenticated.
 */
const AuthGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isAllowed, user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto" />
          <p className="text-slate-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Authed via Clerk but not on the ALLOWED_EMAILS allowlist.
  if (!isAllowed) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-5 rounded-2xl border border-red-500/30 bg-red-950/10 p-8">
          <div className="text-5xl">🔒</div>
          <h1 className="text-xl font-bold text-slate-100">Access Restricted</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            You're signed in as <span className="text-slate-200 font-mono">{user?.email}</span>, but this account isn't on the allowlist.
          </p>
          <p className="text-xs text-slate-500">
            Ask the owner to add your email to <code className="font-mono text-slate-400">ALLOWED_EMAILS</code> and restart the app.
          </p>
          <button
            onClick={() => { void logout(); }}
            className="text-xs font-bold uppercase tracking-widest text-red-400 hover:text-red-300 transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  // Initialize CSRF token on app startup — fetches token from /api/csrf-token
  useCsrfToken();
  const { data: mixTemplates } = useMixTemplates();

  // One-time localStorage -> DB migration (silent, retries on failure)
  useEffect(() => {
    migrateLocalStorageToDb().catch((err: unknown) => {
      console.error('localStorage migration failed:', err);
    });
  }, []);

  const [activeTab, setActiveTab] = useState<string>(() => localStorage.getItem('hh_active_tab') ?? 'studio');
  const [isGenerating, setIsGenerating] = useState(false);
  const [output, setOutput] = useState<ParsedSunoOutput | null>(null);
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [currentBlueprint, setCurrentBlueprint] = useState<string | null>(null);
  const [currentCritique, setCurrentCritique] = useState<MusicCritique | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentMood, setCurrentMood] = useState<string[]>(['Dark', 'Gritty']);
  const [currentBlend, setCurrentBlend] = useState<string[]>(['Americana', 'Southern Rock']);
  const [pickerSlot, setPickerSlot] = useState<'A' | 'B' | null>(null);
  const [narrativeSeed, setNarrativeSeed] = useState<SongSeed | null>(null);
  const [artSeed, setArtSeed] = useState<SongSeed | null>(null);
  const [pendingAlbumTrackSeed, setPendingAlbumTrackSeed] = useState<SongSeed | null>(null);
  const [midiForgeIncomingVibe, setMidiForgeIncomingVibe] = useState<string | null>(null);

  // ─── Onboarding ─────────────────────────────────────────────
  // ─── Context subscriptions ────────────────────────────────

  const { getFullProductionArchitecture, getInstrumentForgePhysics, applyProductionProfile, config: productionConfig, instrumentForgeConfig, applyInstrumentForge, undoProduction, redoProduction, canUndoProduction, canRedoProduction } = useProductionConfig();
  const { config: vocalConfig, getActiveVocalArchitecture, applyBlend, setSinglePack, setHarmonyStack, setChoirGang, undoVocal, redoVocal, canUndoVocal, canRedoVocal } = useVocalConfig();
  const { output: soloOutput, getSoloBlock, getSectionHeader } = useSoloChoreographer();
  const { getRiffDnaBlock, output: riffDnaOutput, config: riffDnaConfig } = useRiffDna();
  const { openCompare, setSlotA, setSlotB } = useCompare();
  const { createSlotFromOutput } = useCreateCompareSlot();
  const { isQuickMode, isAdvancedMode, dispatch: modeDispatch } = useMode();
  const { isTabAvailable, getDefaultTab } = useModeAware();
  const { formData, loadFromRiffWriter, loadFormData, undoForm, redoForm, canUndoForm, canRedoForm, narrativeBlueprint, setNarrativeBlueprint } = useStudioForm();
  const { createSession: createGenSession } = useGenerationSessions();
  const { entries: sessionMemoryEntries, saveSession: saveSessionMemory, deleteSession: deleteSessionMemory } = useSessionMemory();

  // ─── Persona ─────────────────────────────────────────────
  const persona = usePersona();
  const [isPersonaWizardOpen, setIsPersonaWizardOpen] = useState(false);
  const [pendingPersonaIdentity, setPendingPersonaIdentity] = useState<PersonaIdentityPayload | null>(null);
  const [editingPersona, setEditingPersona] = useState<import('@/types/persona.types').Persona | null>(null);

  // ─── Lyric Orchestrator (Parliament Write) ────────────────
  const lyricOrch = useLyricOrchestrator();

  // ─── Lyric Polisher (Parliament Polish) ────────────────
  const lyricPolish = useLyricPolisher();

  // ─── Extracted hooks ──────────────────────────────────────

  const modals = useModalStates();
  const suno = useSunoIntegration({ onSettingsOpen: modals.openSettings });
  // Post-assembly checks that report, never rewrite (Studio Team v2.6.20 handoff items 2 and 3). `withForge`:
  // only the RiffWriter/Album generator carries InstrumentForge {LAYER-2..5} into the lyrics; Parliament write/polish don't.
  const auditDelivery = (result: ParsedSunoOutput, withForge: boolean) => {
    for (const problem of deliveryProblems(result, withForge ? getInstrumentForgePhysics() : undefined)) {
      console.error('[delivery check]', problem);
      suno.showToast(problem, 'error');
    }
  };
  const variations = useVariationChainHandlers(output, suno.sunoCookie);

  // ─── Shortcut reference panel ───────────────────────────────

  const [isShortcutRefOpen, setIsShortcutRefOpen] = useState(false);
  const toggleShortcutRef = useCallback(() => setIsShortcutRefOpen(prev => !prev), []);

  const isAnyModalOpen = modals.isSettingsOpen || modals.isLibraryOpen || modals.isShareModalOpen || modals.isImportModalOpen || modals.isSessionMemoryOpen || modals.isSaveSessionOpen || suno.sunoPrepOpen;

  // ─── Tab management ───────────────────────────────────────

  useEffect(() => {
    if (!isTabAvailable(activeTab)) {
      setActiveTab(getDefaultTab());
    }
  }, [isQuickMode, isTabAvailable, activeTab, getDefaultTab]);

  const handleTabChange = useCallback((tabId: string) => {
    if (tabId === 'compare') { openCompare(); return; }
    setActiveTab(tabId);
    localStorage.setItem('hh_active_tab', tabId);
    if (isAdvancedMode) {
      modeDispatch({ type: 'SET_LAST_TAB', payload: tabId });
    }
    // Track tab visit for checklist
    try {
      const visited = JSON.parse(localStorage.getItem('hookhouse_tabs_visited') || '[]');
      if (!visited.includes(tabId)) {
        visited.push(tabId);
        localStorage.setItem('hookhouse_tabs_visited', JSON.stringify(visited));
      }
    } catch { /* ignore */ }
  }, [openCompare, isAdvancedMode, modeDispatch]);

  // ─── Cross-feature event: open a draft + jump to a tab ──────────
  // Studio Team uses this to "Send to Art" — it saves to Vault and then
  // dispatches this event with { draftId, targetTab }.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ draftId: string; targetTab: string }>).detail;
      if (!detail?.draftId || !detail?.targetTab) return;
      setCurrentDraftId(String(detail.draftId));
      handleTabChange(detail.targetTab);
    };
    window.addEventListener('hookhouse:open-draft', handler);
    return () => window.removeEventListener('hookhouse:open-draft', handler);
  }, [handleTabChange]);

  // ─── Cross-feature event: load a pre-built ParsedSunoOutput ─────
  // Studio Team uses this so Art / Output tabs see a Studio Team song the
  // same way they see a normal Studio generation. Detail: { output, targetTab?, draftId? }
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ output: ParsedSunoOutput; targetTab?: string; draftId?: string }>).detail;
      if (!detail?.output) return;
      setOutput(detail.output);
      // Forgejo #106: explicit reset in the no-id branch too -- otherwise a
      // stale id from whatever was open before this event fired survives
      // into content that was never saved under it.
      setCurrentDraftId(detail.draftId ? String(detail.draftId) : null);
      if (detail.targetTab) handleTabChange(detail.targetTab);
    };
    window.addEventListener('hookhouse:load-output', handler);
    return () => window.removeEventListener('hookhouse:load-output', handler);
  }, [handleTabChange]);

  // Other tabs (Vault, Sessions) ask to be taken somewhere, e.g. "Send to Polish" -> Studio.
  useEffect(() => {
    const handler = (e: Event) => {
      const tabId = (e as CustomEvent<{ tabId?: string }>).detail?.tabId;
      if (tabId) handleTabChange(tabId);
    };
    window.addEventListener('hookhouse:goto-tab', handler);
    return () => window.removeEventListener('hookhouse:goto-tab', handler);
  }, [handleTabChange]);

  // ─── Suite Bridge v2 ────────────────────────────────────────
  useSuiteBridge({
    loadFormData, loadFromRiffWriter, formData, activeTab, handleTabChange, showToast: suno.showToast,
    onReceivePersonaIdentity: useCallback((payload: PersonaIdentityPayload) => {
      setPendingPersonaIdentity(payload);
      setIsPersonaWizardOpen(true);
    }, []),
  });

  // Check URL for share code on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shareCode = params.get('code');
    if (shareCode) {
      modals.openImport();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // ─── Core generation handlers ─────────────────────────────

  // Same persona fields for every song-architect call (generate, album, regenerate).
  const buildPersonaOverride = () => persona.activePersona ? {
    name: persona.activePersona.name,
    vocalArchitecture: persona.activePersona.vocalArchitecture,
    productionArchitecture: persona.activePersona.productionArchitecture,
    bandIdentityBlock: persona.activePersona.bandIdentityBlock,
    useLockedTemplate: persona.activePersona.useLockedTemplate,
    styleTemplate: persona.activePersona.styleTemplate,
    vocalPhysicsLocked: persona.activePersona.vocalPhysicsLocked,
    rhythmPhysicsGuidance: persona.activePersona.rhythmPhysicsGuidance,
    defaultBpm: persona.activePersona.defaultBpm,
    defaultKey: persona.activePersona.defaultKey,
  } : null;

  const handleGenerate = async (data: HookhouseFormData, vocalArchitecture?: string) => {
    setIsGenerating(true);
    setError(null);
    setOutput(null);
    // Forgejo #106: a stale id from the PREVIOUS song must not survive into
    // a new generation -- an edit made before this one's own auto-save
    // completes would otherwise silently PUT onto the old draft's row.
    setCurrentDraftId(null);
    setCurrentBlueprint(null);
    setCurrentCritique(null);
    setActiveTab('output');

    // Fire-and-forget bump for style preset usage tracking (only when a preset
    // is actually being applied, not bypassed by an active persona).
    if (data.stylePreset && data.stylePreset !== 'None' && !persona.activePersona) {
      fetchWithCsrf('/api/style-presets/used', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: data.stylePreset }),
      }).catch(() => {});
    }

    const prodArch = getFullProductionArchitecture();
    const riffDnaBlock = riffDnaOutput ? getRiffDnaBlock() : '';
    const soloBlock = soloOutput ? `${getSectionHeader()}\n${getSoloBlock()}` : '';
    const narrativeArch = narrativeBlueprint ? buildNarrativeBlock(narrativeBlueprint) : undefined;

    const personaOverride = buildPersonaOverride();

    try {
      let result = await generateSunoPrompt(data, vocalArchitecture, prodArch, riffDnaBlock, soloBlock, narrativeArch, personaOverride);
      result.lyricsWithTags = postProcessPhysicsLayers(result.lyricsWithTags, vocalArchitecture, getInstrumentForgePhysics() || undefined);
      if (soloOutput) {
        result.lyricsWithTags = postProcessLyrics(result.lyricsWithTags, { soloOutput });
      }
      if (riffDnaOutput) {
        result = postProcessRiffDna(result, riffDnaConfig);
      }
      result = finalizeGeneratedResult(result);
      auditDelivery(result, true);
      const unique = await ensureUniqueSongTitle(result);
      result = unique.result;
      if (unique.changedFrom) suno.showToast(`"${unique.changedFrom}" was already in your Vault -- retitled "${result.title}"`, 'success');
      setOutput(result);
      localStorage.setItem('hookhouse_has_generated', 'true');

      // Auto-save to vault + create generation session
      try {
        const saveData = await saveToVault(result, 'generated');
        if (saveData?.id) setCurrentDraftId(String(saveData.id));
        adoptSavedSummary(saveData?.summary, result.summary);
        suno.showToast(`"${result.title}" auto-saved to Vault!`, 'success');
        await createGenSession({
          draftId: saveData?.id ? String(saveData.id) : undefined,
          vocalConfigSnapshot: JSON.stringify(vocalConfig),
          productionConfigSnapshot: JSON.stringify(productionConfig),
          formDataSnapshot: JSON.stringify(data),
          vocalPackKey: vocalConfig.mode === 'blend'
            ? `Blend: ${vocalConfig.blendConfig?.packAKey || ''} + ${vocalConfig.blendConfig?.packBKey || ''}`
            : vocalConfig.singlePackKey || undefined,
          genre: data.blends?.[0] || currentBlend[0] || undefined,
          tempo: productionConfig?.selectedTempo || undefined,
          keySignature: productionConfig?.selectedKey
            ? `${productionConfig.selectedKey.root} ${productionConfig.selectedKey.mode}`
            : undefined,
          llmModel: result.llmModel || undefined,
          llmProvider: result.llmProvider || undefined,
        });
      } catch (error: any) {
        console.error('Failed to auto-save to vault:', error);
        suno.showToast(`Failed to auto-save to vault: ${error.message}`, 'error');
      }
    } catch (err: any) {
      let msg = err.message || 'An unexpected error occurred during prompt generation.';
      if (msg.toLowerCase().includes('api key') || msg.toLowerCase().includes('invalid_argument')) {
        const provider = getProvider();
        const providerLabel = { gemini: 'Gemini', claude: 'Claude', openai: 'OpenAI', xai: 'xAI' }[provider] || provider;
        msg = provider === 'gemini'
          ? "Authentication Failure: The system API key is restricted or invalid. Go to 'Settings' and click 'Link Google Cloud API Key' to use your own personal project key."
          : `Authentication Failure: Your ${providerLabel} API key appears invalid, restricted, or expired. Check it in Settings. (Original error: ${err.message || 'unknown'})`;
      }
      setError(msg);
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * The server keeps every machine-written summary to the lyrics-only rule (AUTO-092) and may have rewritten
   * the model's. Show the saved one, so the screen and the Vault agree.
   */
  const adoptSavedSummary = (saved: string | null | undefined, sent: string) => {
    if (typeof saved === 'string' && saved !== sent) setOutput(prev => (prev ? { ...prev, summary: saved } : prev));
  };

  const handleAlbumGenerate = async (data: any, vocalArchitecture: string) => {
    const prodArch = getFullProductionArchitecture();
    const riffDnaBlock = riffDnaOutput ? getRiffDnaBlock() : '';
    const soloBlock = soloOutput ? `${getSectionHeader()}\n${getSoloBlock()}` : '';
    const narrativeArch = narrativeBlueprint ? buildNarrativeBlock(narrativeBlueprint) : undefined;
    const albumPersonaOverride = buildPersonaOverride();
    let result = await generateSunoPrompt(data as HookhouseFormData, vocalArchitecture, prodArch, riffDnaBlock, soloBlock, narrativeArch, albumPersonaOverride);
    result.lyricsWithTags = postProcessPhysicsLayers(result.lyricsWithTags, vocalArchitecture, getInstrumentForgePhysics() || undefined);
    if (soloOutput) {
      result.lyricsWithTags = postProcessLyrics(result.lyricsWithTags, { soloOutput });
    }
    if (riffDnaOutput) {
      result = postProcessRiffDna(result, riffDnaConfig);
    }
    result = finalizeGeneratedResult(result);
    auditDelivery(result, true);
    return (await ensureUniqueSongTitle(result)).result;
  };

  const handleRegenerateWithAdvice = async () => {
    if (!output || !currentCritique) {
      suno.showToast('Missing data for regeneration.', 'error');
      return;
    }
    setIsGenerating(true);
    setError(null);
    setActiveTab('output');
    try {
      // Same post-processing as a fresh generation: vocal identity slot +
      // theme filter + persona lock (dispatcher), then lyrics hard cap and a
      // console script rebuilt from the final Style field (AUTO-040).
      const result = finalizeGeneratedResult(await regenerateWithAdvice(
        output, currentCritique, getActiveVocalArchitecture() || undefined, formData, buildPersonaOverride(),
      ));
      setOutput(result);
      // Forgejo #106: this is a new take saved as its own draft below, not
      // an edit to the one being regenerated from -- drop the old id now so
      // an edit made before the save below completes can't land on it.
      setCurrentDraftId(null);
      setCurrentBlueprint(null);
      setCurrentCritique(null);

      const newDraft: SunoClip = {
        id: `draft_${Date.now()}`,
        title: result.title,
        created_at: new Date().toISOString(),
        model_name: 'Gemini Revision',
        metadata: { tags: result.style, prompt: result.lyricsAlone },
        originalData: result,
      };
      suno.setHistory(prev => {
        const updated = [newDraft, ...prev];
        localStorage.setItem('suno_history', JSON.stringify(updated));
        return updated;
      });

      try {
        const saveData = await saveToVault(result, 'regenerated');
        if (saveData?.id) setCurrentDraftId(String(saveData.id));
        adoptSavedSummary(saveData?.summary, result.summary);
      } catch (error) { console.error('Failed to save to vault:', error); }

      suno.showToast(`Created ${result.title}!`, 'success');
    } catch (err: any) {
      setError(err.message || 'Regeneration failed.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Forgejo #106: this used to be local-only (setOutput, nothing else) --
  // looked saved on screen but silently never reached the DraftVault row.
  // Now PUTs to the same draft first; only updates on-screen state once the
  // write actually succeeds, so a failed save never shows a false "saved"
  // state. Throws on failure -- callers (OutputSection's edit UI) catch it
  // to keep the editor open with the user's text intact.
  const handleLyricsUpdate = async (updatedLyrics: string) => {
    if (!output) return;
    if (!currentDraftId) throw new Error('No saved draft yet -- generate first before editing.');
    const lyricsAlone = updatedLyrics.replace(/\{LAYER-1 VOCAL PHYSICS\}[\s\S]*?\n\n/g, '').replace(/\[.*?\]\n?/g, '').trim();
    await updateDraftField(currentDraftId, { lyricsWithTags: updatedLyrics, lyricsAlone });
    setOutput({ ...output, lyricsWithTags: updatedLyrics, lyricsAlone });
  };

  // Forgejo #106: the other editable output fields (Narrative Summary,
  // Machine Tags, Excluded Styles, Production Blueprint) -- same
  // persist-then-reflect shape as handleLyricsUpdate above.
  const handleOutputFieldUpdate = async (
    field: 'summary' | 'style' | 'excludedStyle' | 'styleBlueprint',
    value: string
  ) => {
    if (!output) return;
    if (!currentDraftId) throw new Error('No saved draft yet -- generate first before editing.');
    await updateDraftField(currentDraftId, { [field]: value });
    setOutput({ ...output, [field]: value });
  };

  // Forgejo #106: loading a draft (e.g. from the Vault) sets `output`
  // directly, bypassing every generation handler above -- without this,
  // currentDraftId stayed whatever it was before (null, or worse, a stale
  // id belonging to a completely different song), so editing a Vault-loaded
  // draft either silently couldn't save or could silently save onto the
  // wrong row. Relies on the caller populating newOutput.draftId (see
  // HHHistoryTab's buildParsedFromDraft) -- falls back to null (editing
  // disabled) for any caller that doesn't yet.
  const handleSelectOutput = (newOutput: ParsedSunoOutput) => {
    setOutput(newOutput);
    setCurrentDraftId(newOutput.draftId ? String(newOutput.draftId) : null);
  };

  // ─── Config / template handlers ───────────────────────────

  const getCurrentConfig = useCallback((): TemplateConfig => ({
    vocalConfig,
    musicalBlend: currentBlend,
    moodArchitecture: currentMood,
    production: productionConfig ? {
      keyTempoMode: productionConfig.keyTempoMode,
      selectedKey: productionConfig.selectedKey
        ? { root: productionConfig.selectedKey.root, mode: productionConfig.selectedKey.mode }
        : null,
      selectedTempo: productionConfig.selectedTempo,
      instrumentStackIds: {
        core: productionConfig.instrumentStack.core.map(i => i.id),
        color: productionConfig.instrumentStack.color.map(i => i.id),
        texture: productionConfig.instrumentStack.texture.map(i => i.id),
      },
      mixTemplateId: productionConfig.mixTemplate?.id || null,
    } : null,
    instrumentForgeConfig,
  }), [vocalConfig, currentBlend, currentMood, productionConfig, instrumentForgeConfig]);

  const handleLoadTemplate = useCallback((template: HookHouseTemplate) => {
    const { config } = template;
    if (config.vocalConfig.mode === 'blend' && config.vocalConfig.blendConfig && config.vocalConfig.blendOutput) {
      applyBlend(config.vocalConfig.blendConfig, config.vocalConfig.blendOutput);
    } else if (config.vocalConfig.singlePackKey) {
      setSinglePack(config.vocalConfig.singlePackKey);
      setHarmonyStack(config.vocalConfig.includeHarmonyStack);
      setChoirGang(config.vocalConfig.includeChoirGang);
    }
    setCurrentBlend(config.musicalBlend);
    setCurrentMood(config.moodArchitecture);
    if (config.instrumentForgeConfig) applyInstrumentForge(config.instrumentForgeConfig);
    if (config.production) {
      applyProductionProfile({
        keyTempoMode: config.production.keyTempoMode,
        selectedKey: config.production.selectedKey
          ? (MUSICAL_KEYS.find(k => k.root === config.production!.selectedKey!.root && k.mode === config.production!.selectedKey!.mode) || null)
          : null,
        selectedTempo: config.production.selectedTempo,
        tempoProfile: null,
        instrumentStack: {
          core: config.production.instrumentStackIds.core.map(id => getInstrumentById(id)).filter(Boolean) as any[],
          color: config.production.instrumentStackIds.color.map(id => getInstrumentById(id)).filter(Boolean) as any[],
          texture: config.production.instrumentStackIds.texture.map(id => getInstrumentById(id)).filter(Boolean) as any[],
          optional: [],
        },
        customInstruments: [],
        mixTemplate: config.production.mixTemplateId ? (getMixTemplateById(config.production.mixTemplateId, mixTemplates) || null) : null,
        customPositions: [],
        humanization: config.production.humanization ?? 0,
        suggestion: null,
      });
    }
    suno.showToast(`Successfully loaded template: ${template.name}`, 'success');
  }, [applyBlend, setSinglePack, setHarmonyStack, setChoirGang, applyProductionProfile, applyInstrumentForge, suno.showToast]);

  const handleShareImportApply = useCallback((config: TemplateConfig) => {
    handleLoadTemplate({ id: 'shared', name: 'Shared Config', description: '', icon: '', tags: [], createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(), config });
    modals.closeImport();
  }, [handleLoadTemplate, modals.closeImport]);

  // ─── Session memory handlers ──────────────────────────────

  const getCurrentSnapshot = useCallback((): SessionMemorySnapshot => ({
    templateConfig: getCurrentConfig(),
    formData,
    activeTab,
    lastOutput: output,
  }), [getCurrentConfig, formData, activeTab, output]);

  const handleRecallSession = useCallback((entry: import('@/types/sessionMemory.types').SessionMemoryEntry) => {
    handleLoadTemplate({ id: entry.id, name: entry.name, description: entry.description, icon: entry.icon, tags: entry.tags, createdAt: entry.createdAt, updatedAt: entry.updatedAt, config: entry.snapshot.templateConfig });
    loadFormData(entry.snapshot.formData);
    if (entry.snapshot.lastOutput) setOutput(entry.snapshot.lastOutput);
    if (entry.snapshot.activeTab && isTabAvailable(entry.snapshot.activeTab)) setActiveTab(entry.snapshot.activeTab);
    modals.closeSessionMemory();
    suno.showToast(`Recalled session: ${entry.name}`, 'success');
  }, [handleLoadTemplate, loadFormData, isTabAvailable, suno.showToast, modals.closeSessionMemory]);

  const handleSaveSession = useCallback((name: string, description: string, icon: string, tags: string[]) => {
    saveSessionMemory(name, description, icon, tags, getCurrentSnapshot());
    suno.showToast(`Session "${name}" saved!`, 'success');
  }, [saveSessionMemory, getCurrentSnapshot, suno.showToast]);

  // ─── Misc handlers ────────────────────────────────────────

  const handleCopyFromRiffWriter = useCallback((seed: SongSeed) => {
    loadFromRiffWriter({
      title: seed.title, concept: seed.concept, povSetting: seed.povSetting,
      mustHaves: seed.mustHaves, avoid: seed.avoid, instruments: seed.instruments,
      structure: seed.structure, bpm: seed.bpm, key: seed.key,
    });
    suno.showToast(`"${seed.title}" loaded into Studio — switch tabs when ready`, 'success');
  }, [loadFromRiffWriter, suno.showToast]);

  const handlePersonaSave = useCallback(async (data: PersonaWizardData): Promise<boolean> => {
    if (editingPersona) {
      const result = await persona.updatePersonaFromWizard(editingPersona.id, data);
      if (result) {
        suno.showToast(`Persona "${result.name}" updated!`, 'success');
        return true;
      }
      suno.showToast(persona.lastError.current || 'Failed to update persona.', 'error');
      return false;
    }
    const result = await persona.createPersona(data);
    if (result) {
      persona.setActivePersonaId(result.id);
      suno.showToast(`Persona "${result.name}" created and activated!`, 'success');
      return true;
    }
    suno.showToast(persona.lastError.current || 'Failed to save persona.', 'error');
    return false;
  }, [editingPersona, persona.createPersona, persona.updatePersonaFromWizard, persona.setActivePersonaId, persona.lastError, suno.showToast]);

  const handleBuildNarrative = useCallback((seed: SongSeed) => {
    setNarrativeSeed(seed);
    handleTabChange('narrative');
  }, [handleTabChange]);

  const { setBrief: setStudioTeamBrief } = useStudioTeam();
  const handleSendToStudioTeam = useCallback((seed: SongSeed) => {
    // seed.genre is a RiffWriter module slug (e.g. "capricorn"). Map to a
    // real-world genre label so Studio Team's agents see "'70s Southern Rock"
    // instead of "Capricorn Swamp Forge".
    const primaryDisplay = getGenreFamily(seed.genre);

    let genreField = primaryDisplay;
    const constraintParts: string[] = [];

    if (seed.hybrid) {
      const secondaryDisplay = getGenreFamily(String(seed.hybrid.secondary));
      const rhythmOwner = seed.hybrid.secondaryOwnsRhythm ? secondaryDisplay : primaryDisplay;
      genreField = `${primaryDisplay} × ${secondaryDisplay} (${seed.hybrid.intensity}/${100 - seed.hybrid.intensity} hybrid — rhythm: ${rhythmOwner})`;

      const la = seed.hybrid.layerAttribution;
      constraintParts.push(
        `HYBRID LAYER OWNERSHIP (preserve these — they came from the seed generation, do not override):`,
        `- rhythm: ${la.rhythm}`,
        `- harmony: ${la.harmony}`,
        `- vocals: ${la.vocals}`,
        `- instrumentation: ${la.instrumentation}`,
        `- production: ${la.production}`,
      );
    }

    if (seed.avoid?.length) constraintParts.push(`Avoid: ${seed.avoid.join(', ')}`);

    // Production architecture (Instrument Forge profile, mix template, genre DNA) —
    // every other generation path includes this; without it Studio Team's agents
    // fill the gap with their own invented sonic direction instead of the user's.
    const prodArchForSeed = getFullProductionArchitecture();
    if (prodArchForSeed) {
      constraintParts.push(
        `PRODUCTION ARCHITECTURE (preserve this — it came from the active Instrument Forge profile / mix template, do not override with a different sonic direction):`,
        prodArchForSeed,
      );
    }

    setStudioTeamBrief({
      title: seed.title,
      genre: genreField,
      mood: seed.mustHaves?.slice(0, 3).join(', ') ?? '',
      setting: seed.povSetting ?? '',
      vocal: seed.vocalStyle ?? '',
      constraints: constraintParts.join('\n'),
      includeImagePrompt: false,
    });
    handleTabChange('studio_team');
  }, [setStudioTeamBrief, handleTabChange, getFullProductionArchitecture]);

  const handleSendStoryToStudioTeam = useCallback((card: SituationCard) => {
    const prodArchForStory = getFullProductionArchitecture();
    const storyConstraints = [`Story scenario: ${card.scenario}`];
    if (prodArchForStory) {
      storyConstraints.push(
        '',
        `PRODUCTION ARCHITECTURE (preserve this — it came from the active Instrument Forge profile / mix template, do not override with a different sonic direction):`,
        prodArchForStory,
      );
    }
    setStudioTeamBrief({
      title: card.title,
      genre: card.genre,
      mood: card.mood,
      setting: card.setting,
      constraints: storyConstraints.join('\n'),
      includeImagePrompt: false,
    });
    handleTabChange('studio_team');
  }, [setStudioTeamBrief, handleTabChange, getFullProductionArchitecture]);

  const handleSendToArt = useCallback((seed: SongSeed) => {
    setArtSeed(seed);
    handleTabChange('image');
  }, [handleTabChange]);

  const handleSendToAlbum = useCallback((seed: SongSeed) => {
    setPendingAlbumTrackSeed(seed);
    handleTabChange('album');
  }, [handleTabChange]);

  const handleSendToMidiForge = useCallback((seed: SongSeed) => {
    const genreDisplay = seed.genre.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    const parts: string[] = [];
    parts.push(`A ${genreDisplay} track from the ${seed.era} era.`);
    if (seed.concept) parts.push(seed.concept);
    if (seed.vocalStyle) parts.push(`Vocal style: ${seed.vocalStyle}.`);
    if (seed.instruments?.length) parts.push(`Instrumentation: ${seed.instruments.join(', ')}.`);
    if (seed.structure) parts.push(`Structure: ${seed.structure}.`);
    if (seed.bpm && seed.key) parts.push(`${seed.bpm} BPM in ${seed.key}.`);
    if (seed.userTheme) parts.push(`Theme: ${seed.userTheme}`);
    setMidiForgeIncomingVibe(parts.join(' '));
    handleTabChange('midiforge');
  }, [handleTabChange]);

  const handleSendInstrumentalToStudio = useCallback((lyrics: string, title: string) => {
    setOutput(prev => prev ? {
      ...prev,
      title,
      lyricsWithTags: lyrics,
      lyricsAlone: lyrics.replace(/\[.*?\]\n?/g, '').trim(),
    } : {
      titleArtist: title,
      summary: '',
      styleBlueprint: '',
      style: '',
      excludedStyle: '',
      title,
      artist: '',
      lyricsWithTags: lyrics,
      lyricsAlone: lyrics.replace(/\[.*?\]\n?/g, '').trim(),
      javascriptCode: '',
      fullResponse: '',
    });
    handleTabChange('studio');
  }, [handleTabChange]);

  const handleInjectNarrative = useCallback((blueprint: NarrativeBlueprint) => {
    setNarrativeBlueprint(blueprint);
    handleTabChange('studio');
  }, [setNarrativeBlueprint, handleTabChange]);

  const handleSendNarrativeToStudioTeam = useCallback((blueprint: NarrativeBlueprint) => {
    const seed = narrativeSeed;
    if (!seed) return;

    const primaryDisplay = getGenreFamily(seed.genre);

    let genreField = primaryDisplay;
    const constraintParts: string[] = [];

    // Narrative block first — character + arc + tone are the spine of the brief
    constraintParts.push(
      `NARRATIVE BLUEPRINT (preserve the character, arc, and tone — these came from the Narrative Engine, do not override):`,
      buildNarrativeBlock(blueprint),
    );

    if (seed.hybrid) {
      const secondaryDisplay = getGenreFamily(String(seed.hybrid.secondary));
      const rhythmOwner = seed.hybrid.secondaryOwnsRhythm ? secondaryDisplay : primaryDisplay;
      genreField = `${primaryDisplay} × ${secondaryDisplay} (${seed.hybrid.intensity}/${100 - seed.hybrid.intensity} hybrid — rhythm: ${rhythmOwner})`;
      const la = seed.hybrid.layerAttribution;
      constraintParts.push(
        '',
        `HYBRID LAYER OWNERSHIP (preserve these — they came from the seed generation, do not override):`,
        `- rhythm: ${la.rhythm}`,
        `- harmony: ${la.harmony}`,
        `- vocals: ${la.vocals}`,
        `- instrumentation: ${la.instrumentation}`,
        `- production: ${la.production}`,
      );
    }

    if (seed.avoid?.length) constraintParts.push('', `Avoid: ${seed.avoid.join(', ')}`);

    // Production architecture (Instrument Forge profile, mix template, genre DNA) —
    // every other generation path (Studio, Album, Lyric Orchestrator/Polish) includes
    // this; it was missing here, so Studio Team never saw the user's actual production
    // intent and ARIA/RIFF filled the gap with their own invented setting/vibe instead.
    const prodArch = getFullProductionArchitecture();
    if (prodArch) {
      constraintParts.push(
        '',
        `PRODUCTION ARCHITECTURE (preserve this — it came from the active Instrument Forge profile / mix template, do not override with a different sonic direction):`,
        prodArch,
      );
    }

    // Vocal: combine the seed's vocal style with the character's voice texture
    const vocalParts = [seed.vocalStyle, blueprint.character.voiceTexture].filter(Boolean);

    setStudioTeamBrief({
      title: seed.title,
      genre: genreField,
      mood: `${blueprint.tone.label}; ${seed.mustHaves?.slice(0, 3).join(', ') ?? ''}`.trim(),
      setting: seed.povSetting ?? '',
      vocal: vocalParts.join(' — '),
      constraints: constraintParts.join('\n'),
      includeImagePrompt: false,
    });
    handleTabChange('studio_team');
  }, [narrativeSeed, setStudioTeamBrief, handleTabChange, getFullProductionArchitecture]);

  // ─── Parliament Write handlers ─────────────────────────────

  const handleParliamentWrite = useCallback(async (data: HookhouseFormData, vocalArchitecture?: string) => {
    setOutput(null);
    setCurrentDraftId(null); // Forgejo #106: see handleGenerate's comment
    setActiveTab('output');
    lyricPolish.reset(); // Mutual exclusion: clear any active polish
    const genre = data.blends?.[0] || currentBlend[0] || 'Southern Rock';
    const moods = data.mood?.length ? data.mood : currentMood;

    const prodArch = getFullProductionArchitecture();
    const riffDnaBlock = riffDnaOutput ? getRiffDnaBlock() : '';
    const soloBlock = soloOutput ? `${getSectionHeader()}\n${getSoloBlock()}` : '';
    const narrativeArch = narrativeBlueprint ? buildNarrativeBlock(narrativeBlueprint) : undefined;
    // buildPersonaOverride reads persona.activePersona, which is in the deps below.
    const personaOverride = buildPersonaOverride();

    await lyricOrch.startLyricDeliberation(data.seed, genre, moods, {
      vocalArchitecture,
      productionArchitecture: prodArch,
      riffDnaBlock,
      soloBlock,
      narrativeBlock: narrativeArch,
      personaOverride,
    });
  }, [currentBlend, currentMood, lyricOrch.startLyricDeliberation, lyricPolish.reset, getFullProductionArchitecture, riffDnaOutput, getRiffDnaBlock, soloOutput, getSectionHeader, getSoloBlock, narrativeBlueprint, persona.activePersona]);

  const handleAcceptParliamentLyrics = useCallback(() => {
    if (!lyricOrch.result) return;
    const vocalArch = getActiveVocalArchitecture();
    let parsed = toLyricOutput(lyricOrch.result, formData, vocalArch);
    parsed.lyricsWithTags = postProcessPhysicsLayers(parsed.lyricsWithTags, vocalArch, getInstrumentForgePhysics() || undefined);
    if (riffDnaOutput) {
      parsed = postProcessRiffDna(parsed, riffDnaConfig);
    }
    parsed = finalizeGeneratedResult(parsed);
    auditDelivery(parsed, false);
    setOutput(parsed);
    lyricOrch.reset();

    // Auto-save to vault
    saveToVault(parsed, 'generated')
      .then((saveData) => {
        if (saveData?.id) setCurrentDraftId(String(saveData.id));
        suno.showToast(`"${parsed.title}" saved to Vault!`, 'success');
      })
      .catch((err: unknown) => console.error('Failed to save parliament lyrics to vault:', err));
  }, [lyricOrch.result, lyricOrch.reset, formData, suno.showToast, getActiveVocalArchitecture, getInstrumentForgePhysics, riffDnaOutput, riffDnaConfig]);

  // ─── Parliament Polish handlers ─────────────────────────────

  const handleParliamentPolish = useCallback(async (lyrics: string, data: HookhouseFormData, vocalArchitecture?: string) => {
    setOutput(null);
    setCurrentDraftId(null); // Forgejo #106: see handleGenerate's comment
    setActiveTab('output');
    lyricOrch.reset(); // Mutual exclusion: clear any active write
    const genre = data.blends?.[0] || currentBlend[0] || 'Southern Rock';
    const moods = data.mood?.length ? data.mood : currentMood;

    const prodArch = getFullProductionArchitecture();
    const riffDnaBlock = riffDnaOutput ? getRiffDnaBlock() : '';
    const soloBlock = soloOutput ? `${getSectionHeader()}\n${getSoloBlock()}` : '';
    const narrativeArch = narrativeBlueprint ? buildNarrativeBlock(narrativeBlueprint) : undefined;
    // buildPersonaOverride reads persona.activePersona, which is in the deps below.
    const personaOverride = buildPersonaOverride();

    await lyricPolish.startPolish(lyrics, genre, moods, {
      vocalArchitecture,
      productionArchitecture: prodArch,
      riffDnaBlock,
      soloBlock,
      narrativeBlock: narrativeArch,
      personaOverride,
    });
  }, [currentBlend, currentMood, lyricOrch.reset, lyricPolish.startPolish, getFullProductionArchitecture, riffDnaOutput, getRiffDnaBlock, soloOutput, getSectionHeader, getSoloBlock, narrativeBlueprint, persona.activePersona]);

  const handleAcceptPolishedLyrics = useCallback(() => {
    if (!lyricPolish.result) return;
    const vocalArch = getActiveVocalArchitecture();
    let parsed = toPolishOutput(lyricPolish.result, formData, vocalArch);
    parsed.lyricsWithTags = postProcessPhysicsLayers(parsed.lyricsWithTags, vocalArch, getInstrumentForgePhysics() || undefined);
    if (riffDnaOutput) {
      parsed = postProcessRiffDna(parsed, riffDnaConfig);
    }
    // A song sent over from the Vault with Studio Team cues: put its cue/dossier header back in front.
    parsed.lyricsWithTags = attachStudioTeamScaffold(takeScaffold(), parsed.lyricsWithTags);
    parsed = finalizeGeneratedResult(parsed);
    auditDelivery(parsed, false);
    setOutput(parsed);
    lyricPolish.reset();

    // Auto-save to vault
    saveToVault(parsed, 'generated')
      .then((saveData) => {
        if (saveData?.id) setCurrentDraftId(String(saveData.id));
        suno.showToast(`"${parsed.title}" saved to Vault!`, 'success');
      })
      .catch((err: unknown) => console.error('Failed to save polished lyrics to vault:', err));
  }, [lyricPolish.result, lyricPolish.reset, formData, suno.showToast, getActiveVocalArchitecture, getInstrumentForgePhysics, riffDnaOutput, riffDnaConfig]);

  const handleSelectForComparison = (slot: 'A' | 'B') => {
    if (suno.history.length === 0) {
      suno.showToast('Vault is empty. Generate or sync songs first.', 'error');
      return;
    }
    setPickerSlot(slot);
  };

  const handleFinalSelectionForSlot = (clip: SunoClip) => {
    if (!pickerSlot) return;
    if (clip.originalData) {
      const compareSlot = createSlotFromOutput(clip.originalData, clip.id);
      if (pickerSlot === 'A') setSlotA(compareSlot);
      else setSlotB(compareSlot);
    } else {
      const basicSlot = {
        id: clip.id,
        timestamp: new Date(clip.created_at),
        source: 'history' as const,
        title: clip.title,
        lyrics: clip.metadata?.prompt || 'No lyrics found.',
        vocalPhysicsBlocks: [],
        sunoStyleTags: clip.metadata?.tags?.split(',').map(t => t.trim()) || [],
      };
      if (pickerSlot === 'A') setSlotA(basicSlot);
      else setSlotB(basicSlot);
    }
    suno.showToast(`Loaded "${clip.title}" into Slot ${pickerSlot}`, 'success');
    setPickerSlot(null);
  };

  // ─── Global keyboard shortcuts ───────────────────────────

  const handleUndo = useCallback(() => {
    if (activeTab === 'studio') undoForm();
    else if (activeTab === 'vocals') undoVocal();
    else if (activeTab === 'production') undoProduction();
  }, [activeTab, undoForm, undoVocal, undoProduction]);

  const handleRedo = useCallback(() => {
    if (activeTab === 'studio') redoForm();
    else if (activeTab === 'vocals') redoVocal();
    else if (activeTab === 'production') redoProduction();
  }, [activeTab, redoForm, redoVocal, redoProduction]);

  useGlobalShortcuts({
    onTabChange: handleTabChange,
    onGenerate: () => handleGenerate(formData),
    isGenerating,
    activeTab,
    isAnyModalOpen,
    onToggleSettings: modals.toggleSettings,
    onToggleLibrary: modals.toggleLibrary,
    onToggleSessionMemory: modals.toggleSessionMemory,
    onToggleShortcutRef: toggleShortcutRef,
    onUndo: handleUndo,
    onRedo: handleRedo,
  });

  // ─── Render ───────────────────────────────────────────────

  return (
    <div className="min-h-screen relative overflow-hidden bg-slate-950 text-slate-50 font-sans selection:bg-accent selection:text-black">
      <Header
        sunoBilling={suno.sunoBilling}
        sunoAuthStatus={suno.sunoAuthStatus}
        sunoTokenMinutesRemaining={suno.sunoTokenMinutesRemaining}
        onSettingsClick={modals.toggleSettings}
        onLibraryClick={modals.openLibrary}
        isSettingsOpen={modals.isSettingsOpen}
        onShortcutRefClick={toggleShortcutRef}
      />

      <AdminPage
        isOpen={modals.isSettingsOpen}
        onClose={modals.closeSettings}
        sunoCookie={suno.sunoCookie}
        onSave={suno.handleSaveSettings}
        onVocalPacksUpdated={() => window.location.reload()}
      />

      <TemplatePanel
        isOpen={modals.isLibraryOpen}
        onClose={modals.closeLibrary}
        onLoadTemplate={handleLoadTemplate}
        getCurrentConfig={getCurrentConfig}
      />

      {/* Floating Side Tab for Templates */}
      <button
        onClick={modals.openLibrary}
        className="fixed left-0 top-1/2 -translate-y-1/2 z-[55] bg-slate-900 border-y border-r border-slate-700 py-6 px-1.5 rounded-r-xl text-slate-400 hover:text-accent hover:bg-slate-800 transition-all hover:pr-3 group"
      >
        <div className="flex flex-col items-center gap-4">
          <Library size={16} />
          <span className="[writing-mode:vertical-lr] text-[11px] font-black uppercase tracking-[0.2em] group-hover:tracking-[0.3em] transition-all">Templates</span>
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-52 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-[60]">
          <p className="text-[11px] font-bold text-amber-400 mb-1">Preset Configurations</p>
          <p className="text-[10px] text-slate-400 leading-relaxed">Save and reuse vocal + production knob settings as reusable starting points for new songs.</p>
        </div>
      </button>

      {/* Floating Side Tab for Session Memory */}
      <button
        onClick={modals.openSessionMemory}
        className="fixed left-0 top-[calc(50%+120px)] -translate-y-1/2 z-[55] bg-slate-900 border-y border-r border-slate-700 py-6 px-1.5 rounded-r-xl text-slate-400 hover:text-accent hover:bg-slate-800 transition-all hover:pr-3 group"
      >
        <div className="flex flex-col items-center gap-4">
          <Clock size={16} />
          <span className="[writing-mode:vertical-lr] text-[11px] font-black uppercase tracking-[0.2em] group-hover:tracking-[0.3em] transition-all">Memory</span>
          <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </div>
        <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-52 px-3 py-2.5 rounded-lg bg-slate-800 border border-slate-700 shadow-xl opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 z-[60]">
          <p className="text-[11px] font-bold text-amber-400 mb-1">Full Session Snapshots</p>
          <p className="text-[10px] text-slate-400 leading-relaxed">Save your entire creative state — config, seed text, lyrics, generated output, and active tab — to resume exactly where you left off.</p>
        </div>
      </button>

      <SessionMemoryPanel
        isOpen={modals.isSessionMemoryOpen}
        onClose={modals.closeSessionMemory}
        entries={sessionMemoryEntries}
        onRecall={handleRecallSession}
        onDelete={deleteSessionMemory}
        onSaveCurrent={modals.openSaveSession}
      />
      <SaveSessionModal
        isOpen={modals.isSaveSessionOpen}
        onClose={modals.closeSaveSession}
        onSave={handleSaveSession}
      />

      <CompareView onSelectFromHistory={handleSelectForComparison} />

      {pickerSlot && (
        <VaultPicker
          isOpen={true}
          onClose={() => setPickerSlot(null)}
          history={suno.history}
          onSelect={handleFinalSelectionForSlot}
          slotLabel={pickerSlot}
        />
      )}

      <main className="w-full pl-16 pr-6 py-8 relative z-10">
        <AdaptiveTabNav activeTab={activeTab} onTabChange={handleTabChange} outputAvailable={!!output} />

        <div className="animate-in fade-in duration-500 w-full min-h-[600px]">
          <TabRenderer
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isGenerating={isGenerating}
            output={output}
            error={error}
            currentBlueprint={currentBlueprint}
            currentCritique={currentCritique}
            isQuickMode={isQuickMode}
            formData={formData}
            currentBlend={currentBlend}
            currentMood={currentMood}
            onMoodChange={setCurrentMood}
            onBlendChange={setCurrentBlend}
            onGenerate={handleGenerate}
            onAlbumGenerate={handleAlbumGenerate}
            onRegenerateWithAdvice={handleRegenerateWithAdvice}
            onLyricsUpdate={handleLyricsUpdate}
            onOutputFieldUpdate={handleOutputFieldUpdate}
            onCopyFromRiffWriter={handleCopyFromRiffWriter}
            onSelectOutput={handleSelectOutput}
            onBlueprintGenerated={setCurrentBlueprint}
            onCritiqueGenerated={setCurrentCritique}
            onLoadTemplate={handleLoadTemplate}
            getCurrentConfig={getCurrentConfig}
            onShareOpen={modals.openShare}
            onSettingsOpen={modals.openSettings}
            onLibraryOpen={modals.openLibrary}
            sunoCookie={suno.sunoCookie}
            isSyncing={suno.isSyncing}
            history={suno.history}
            onDirectSync={suno.handleDirectSync}
            openSunoPrep={suno.openSunoPrep}
            fetchAndMergeSunoHistory={suno.fetchAndMergeSunoHistory}
            narrativeSeed={narrativeSeed}
            onBuildNarrative={handleBuildNarrative}
            onSendToStudioTeam={handleSendToStudioTeam}
            onSendStoryToStudioTeam={handleSendStoryToStudioTeam}
            onSendToArt={handleSendToArt}
            onSendToAlbum={handleSendToAlbum}
            onSendToMidiForge={handleSendToMidiForge}
            artSeed={artSeed}
            pendingAlbumTrackSeed={pendingAlbumTrackSeed}
            onPendingAlbumTrackConsumed={() => setPendingAlbumTrackSeed(null)}
            midiForgeIncomingVibe={midiForgeIncomingVibe}
            onMidiForgeVibeConsumed={() => setMidiForgeIncomingVibe(null)}
            onSendInstrumentalToStudio={handleSendInstrumentalToStudio}
            onInjectNarrative={handleInjectNarrative}
            onSendNarrativeToStudioTeam={handleSendNarrativeToStudioTeam}
            activeChain={variations.activeChain}
            getTree={variations.getTree}
            isChainLoading={variations.isChainLoading}
            onStartVariationChain={variations.handleStartVariationChain}
            onSpawnVariations={variations.handleSpawnVariations}
            onRateVariationNode={variations.handleRateVariationNode}
            onTriggerNodeGen={variations.handleTriggerNodeGen}
            onDeleteChain={variations.handleDeleteChain}
            activePersona={persona.activePersona}
            personas={persona.personas}
            activePersonaId={persona.activePersonaId}
            onPersonaSelect={persona.setActivePersonaId}
            onPersonaCreateNew={() => { setPendingPersonaIdentity(null); setEditingPersona(null); setIsPersonaWizardOpen(true); }}
            onPersonaDelete={persona.deletePersona}
            onPersonaEdit={(p) => { setEditingPersona(p); setPendingPersonaIdentity(null); setIsPersonaWizardOpen(true); }}
            onParliamentWrite={handleParliamentWrite}
            isParliamentWriting={lyricOrch.isWriting}
            lyricOrchStatus={lyricOrch.status}
            lyricOrchResult={lyricOrch.result}
            lyricOrchError={lyricOrch.error}
            lyricOrchProgress={lyricOrch.progress}
            lyricOrchCompletedAgents={lyricOrch.completedAgents}
            onLyricOrchCancel={lyricOrch.cancel}
            onLyricOrchReset={lyricOrch.reset}
            onLyricOrchAccept={handleAcceptParliamentLyrics}
            onParliamentPolish={handleParliamentPolish}
            isParliamentPolishing={lyricPolish.isPolishing}
            lyricPolishStatus={lyricPolish.status}
            lyricPolishResult={lyricPolish.result}
            lyricPolishError={lyricPolish.error}
            lyricPolishProgress={lyricPolish.progress}
            lyricPolishCompletedAgents={lyricPolish.completedAgents}
            onLyricPolishCancel={lyricPolish.cancel}
            onLyricPolishReset={lyricPolish.reset}
            onLyricPolishAccept={handleAcceptPolishedLyrics}
            currentDraftId={currentDraftId}
          />
        </div>
      </main>

      <Footer />

      <ShareCodeModal isOpen={modals.isShareModalOpen} onClose={modals.closeShare} config={getCurrentConfig()} />
      <ImportCodeModal isOpen={modals.isImportModalOpen} onClose={modals.closeImport} onApply={handleShareImportApply} />
      <ShortcutReferencePanel isOpen={isShortcutRefOpen} onClose={() => setIsShortcutRefOpen(false)} />
      <SunoPrepModal
        isOpen={suno.sunoPrepOpen}
        onClose={suno.handleSunoPrepClose}
        onGenerate={suno.handleSunoPrepGenerate}
        initialPayload={suno.sunoPrepPayload}
        isGenerating={suno.sunoPrepGenerating}
        billing={suno.sunoBilling}
        onUpsample={suno.handleTagUpsample}
      />
      <GenerationProgress
        generation={suno.activeGeneration}
        onDismiss={suno.dismissGeneration}
        onCancel={suno.cancelGeneration}
      />

      <PersonaWizard
        isOpen={isPersonaWizardOpen}
        onClose={() => { setIsPersonaWizardOpen(false); setPendingPersonaIdentity(null); setEditingPersona(null); }}
        onSave={handlePersonaSave}
        pendingPayload={pendingPersonaIdentity}
        editingPersona={editingPersona}
      />

    </div>
  );
};

const App: React.FC = () => (
  <BrowserRouter>
    <AuthProvider>
      <Routes>
        {/* Single-user mode: root redirects straight into the app. AuthGuard inside /app/* bounces unauthenticated users to /login. */}
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route
          path="/app/*"
          element={
            <AuthGuard>
              <ToastProvider>
                <ModeProvider>
                  <TemplateProvider>
                    <StudioFormProvider>
                      <VocalConfigProvider>
                        <ProductionConfigProvider>
                          <CompareProvider>
                            <ReferenceTrackProvider>
                              <SoloChoreographerProvider>
                                <RiffDnaProvider>
                                  <AppContent />
                                </RiffDnaProvider>
                              </SoloChoreographerProvider>
                            </ReferenceTrackProvider>
                          </CompareProvider>
                        </ProductionConfigProvider>
                      </VocalConfigProvider>
                    </StudioFormProvider>
                  </TemplateProvider>
                </ModeProvider>
              </ToastProvider>
            </AuthGuard>
          }
        />
      </Routes>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
